'use client';

import { mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/global-config';

import { logoClasses } from './classes';
import { BrandCube } from './brand-cube';

/** 워드마크 옆 큐브 한 변(px) — 바탕 없이 칸을 채우므로 글자 높이(20px)보다 조금 크게 잡는다 */
const ICON_SIZE = 28;

/** 큐브 기본 표시 — PC(`md` 이상)에서만 보인다 */
const ICON_SX_DEFAULT = { display: { xs: 'none', md: 'block' } };

// ----------------------------------------------------------------------

export type LogoProps = {
  sx?: any;
  disabled?: boolean;
  className?: string;
  href?: string;
  isSingle?: boolean;
  /**
   * 워드마크 왼쪽에 「Assemble」 모션 큐브(C·M 획 순서대로 칸이 채워진다)를 붙인다(`isSingle={false}` 일 때만, 기본 참).
   * 큐브는 기본으로 PC(`md` 이상)에서만 보인다 — 다른 기준이 필요하면 `iconSx` 로 덮는다.
   */
  withIcon?: boolean;
  iconSx?: any;
} & Record<string, any>;

/**
 * 로고. `isSingle`(기본 참)이면 심볼 이미지 40×40, 아니면 「CatchMoments」 글자 102×36 이다.
 *
 * ⚠️ 링크 주소(`href`, 기본 `/`)와 별개로 클릭하면 `paths.home.root` 로 밀어 넣는다 —
 * `href` 만 바꿔서는 실제 이동지가 안 바뀐다.
 */
export function Logo({ sx, disabled, className, href = '/', isSingle = true, withIcon = true, iconSx = ICON_SX_DEFAULT, ...other }: LogoProps) {

  const router = useRouter();
  const singleLogo = (
    <img
      alt="Single logo"
      src={`${CONFIG.assetsDir}/logo/logo-single.png`}
      width="100%"
      height="100%"
    />
  );

  /*
    🔑 **워드마크는 본문 글꼴을 안 쓴다** — Outfit(기하학적 산세리프)으로 세우고
      「Catch」는 굵게(700) 「Moments」는 가늘게(300) 둔다. 굵기 차가 곧 이름의 마디다.
    ⚠️ 자간을 좁히는 것(-4.5%)이 로고의 일부다 — 기본 자간으로 두면 글자가 흩어져 보인다.
  */
  const fullLogo = (
    <Typography
      component="span"
      sx={{
        fontFamily: '"Outfit Variable", "Outfit", sans-serif',
        fontSize: 20,
        lineHeight: 1.2,
        letterSpacing: '-0.045em',
        color: 'text.primary',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>Catch</Box>
      <Box component="span" sx={{ fontWeight: 300 }}>Moments</Box>
    </Typography>
  );

  return (
    <LogoRoot
      component={RouterLink}
      href={href}
      aria-label="Logo"
      underline="none"
      className={mergeClasses([logoClasses.root, className])}
      sx={[
        {
          width: 40,
          height: 40,
          ...(!isSingle && { width: 102, height: 36 }),
          ...(!isSingle && withIcon && { width: 'auto' }),
          ...(disabled && { pointerEvents: 'none' }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onClick={() => {
        router.push(paths.home.root);
      }}
      {...other}
    >
      {isSingle && singleLogo}
      {!isSingle && (
        /* 🔑 큐브와 글자는 안쪽 한 줄로 묶는다 — 호출부가 바깥에 `display: block` 을 걸어도 세로로 쌓이지 않는다 */
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 1 }}>
          {withIcon && (
            <BrandCube anim="assemble" size={ICON_SIZE} background={false} fit={1.55} sx={iconSx} />
          )}
          {fullLogo}
        </Box>
      )}

    </LogoRoot>
  );
}

// ----------------------------------------------------------------------

const LogoRoot = styled(Link)<{ component?: any; href?: string }>(() => ({
  flexShrink: 0,
  color: 'transparent',
  display: 'inline-flex',
  verticalAlign: 'middle',
}));
